import { useEffect, useState } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import api from "../../lib/api";
import { curatedFor } from "./coverFallbacks";

/**
 * TripCoverSuggestions
 * Shows 3 Unsplash thumbnails suggested from the trip query (title/destination).
 * Members can tap one to auto-fill the trip cover — no more empty grey cards.
 * If the Unsplash proxy is quota-throttled we fall back to a curated set so
 * the row NEVER disappears (see coverFallbacks.js).
 *
 * Props:
 *   query   : string  — search query (usually trip title)
 *   onPick  : (file: File, meta: {photographer,photographer_url,unsplash_url}) => void
 *   testid  : string  — prefix for data-testid on each thumbnail
 */
export default function TripCoverSuggestions({ query, onPick, testid = "trip-cover-suggestion" }) {
  const [photos, setPhotos] = useState(null);
  const [pickingIdx, setPickingIdx] = useState(-1);
  const [pickedIdx, setPickedIdx] = useState(-1);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    if (!query || query.trim().length < 2) { setPhotos([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/unsplash/search", { params: { q: query, per_page: 3 } });
        const results = data?.results?.slice(0, 3) || [];
        if (cancelled) return;
        if (results.length > 0) {
          setPhotos(results); setFallback(false);
        } else {
          setPhotos(curatedFor(query)); setFallback(true);
        }
      } catch {
        if (!cancelled) { setPhotos(curatedFor(query)); setFallback(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [query]);

  const pick = async (idx, photo) => {
    setPickingIdx(idx);
    try {
      // 1) Track the Unsplash download (guideline requirement)
      if (photo.download_location) {
        api.post("/unsplash/track-download", { download_location: photo.download_location }).catch(() => {});
      }
      // 2) Fetch the regular-size image and package as a File
      const res = await fetch(photo.url_regular, { mode: "cors" });
      const blob = await res.blob();
      const safeName = (query || "trip").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
      const file = new File([blob], `${safeName}-unsplash.jpg`, { type: blob.type || "image/jpeg" });
      onPick(file, {
        photographer: photo.photographer,
        photographer_url: photo.photographer_url,
        unsplash_url: photo.unsplash_url,
        source: "unsplash",
      });
      setPickedIdx(idx);
    } catch {
      // Silent — member can still upload their own photo
    }
    setPickingIdx(-1);
  };

  if (!query || query.trim().length < 2) return null;
  if (photos && photos.length === 0) return null;

  return (
    <div className="basis-full mt-2" data-testid="trip-cover-suggestions">
      <div className="text-xs font-700 text-zinc-700 flex items-center gap-1.5 mb-1.5">
        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        {fallback
          ? <>Curated covers — pick one or upload your own</>
          : <>Suggested from Unsplash — one tap to use</>}
      </div>
      <div className="flex gap-2">
        {(photos || Array(3).fill(null)).map((p, idx) => (
          p ? (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(idx, p)}
              disabled={pickingIdx >= 0}
              data-testid={`${testid}-${idx}`}
              className={`relative w-20 h-20 rounded overflow-hidden border-2 transition-all ${pickedIdx === idx ? "border-emerald-500 ring-2 ring-emerald-300" : "border-zinc-300 hover:border-orange-500"} disabled:opacity-60`}
              title={`Photo by ${p.photographer || "photographer"} on Unsplash`}
            >
              <img src={p.url_small} alt={p.alt || query} loading="lazy" className="w-full h-full object-cover" />
              {pickingIdx === idx && <div className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>}
              {pickedIdx === idx && <div className="absolute inset-0 grid place-items-center bg-emerald-500/25"><Check className="w-5 h-5 text-emerald-900" /></div>}
            </button>
          ) : (
            <div key={idx} className="w-20 h-20 rounded bg-zinc-100 animate-pulse" />
          )
        ))}
      </div>
    </div>
  );
}
